import React, { useState, useEffect, useRef, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  Tooltip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ReorderIcon from "@mui/icons-material/Reorder";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SelectAllRoundedIcon from "@mui/icons-material/SelectAllRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import {
  CheckBoxRounded,
  DriveFileRenameOutlineRounded,
  RestoreRounded,
} from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { appendTeamMemberLawyerId, pageScrollStyle } from "../styles/styles";
import axios from "axios";
import { toast } from "react-toastify";
import { useDialog } from "./DialogContext";

const createBulkActionUrl = (baseUrl, actionPath) => {
  const [path, query = ""] = String(baseUrl || "").split("?");
  const normalizedPath = path.endsWith("/") ? path : `${path}/`;
  const finalPath = `${normalizedPath}${actionPath}/`;
  return query ? `${finalPath}?${query}` : finalPath;
};

export default function DataTable({
  extraBtn,
  filterBtn,
  rows,
  columns,
  page,
  setPage,
  pageSize,
  rowCount,
  CustomToolbar,
  CustomNoRowsOverlay,
  height = "100%",
  minHeight = "50vh",
  loading,
  checkboxSelection,
  onSelectionChange,
  onPageChange,
  onPageSizeChange,
  hasNextPage,
  hasPreviousPage,
  handlePageChange,
  filterChips,
  localPageSize,
  handlePageSizeChange,
  showFooter = true,
  token,
  baseDeleteUrl,
  bulkDeleteUrl,
  idField = "id",
  teamMemberLawyerId = null,

  selectionMode = "disabled",
  fetchDataUrl,
  deleteDialogTitle = "Delete Selected Items",
  deleteConfirmMessage = "Are you sure you want to delete the selected items?",
  onRefreshData,
  BulkEditComponent,
  editTitle,
  bulkAction,
  ...rest
}) {
  const { showDialog } = useDialog();
  const resolvedHeight = height || 400;
  const resolvedMinHeight = minHeight ?? "50vh";
  const [currentPage, setCurrentPage] = useState(page || 0);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // State for selection mode
  const [selectionActive, setSelectionActive] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    total: 0,
    completed: 0,
    errors: [],
    failedIds: [],
  });
  const [deleting, setDeleting] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActing, setBulkActing] = useState(false);

  const [loadingAllIds, setLoadingAllIds] = useState(false);
  const resolvedPageSize = localPageSize || pageSize || 10;
  const canChangePageSize =
    typeof handlePageSizeChange === "function" ||
    typeof onPageSizeChange === "function";

  const getStorageKey = () => {
    const pathname = window.location.pathname;
    return `dataTable_columnVisibility_${pathname}`;
  };

  const getColumnOrderKey = () => {
    const pathname = window.location.pathname;
    return `dataTable_columnOrder_${pathname}`;
  };

  useEffect(() => {
    try {
      const storageKey = getStorageKey();
      const orderKey = getColumnOrderKey();
      const savedVisibility = localStorage.getItem(storageKey);
      const savedOrder = localStorage.getItem(orderKey);

      if (savedVisibility) {
        setColumnVisibilityModel(JSON.parse(savedVisibility));
      }

      if (savedOrder) {
        const parsedOrder = JSON.parse(savedOrder);
        setColumnOrder(parsedOrder);
      } else {
        setColumnOrder(columns.map((col) => col.field));
      }
    } catch (error) {
      console.error("Error loading column settings from localStorage:", error);
      setColumnOrder(columns.map((col) => col.field));
    }
  }, [columns]);

  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  // Determine if checkboxes should be shown
  const showCheckboxes = useMemo(() => {
    if (selectionMode === "always") return true;
    if (selectionMode === "clickToActivate" && selectionActive) return true;
    return checkboxSelection || false;
  }, [selectionMode, selectionActive, checkboxSelection]);

  // Apply column order to create ordered columns
  const orderedColumns = React.useMemo(() => {
    if (!columnOrder.length) return columns;

    return columnOrder
      .map((field) => columns.find((col) => col.field === field))
      .filter(Boolean)
      .concat(columns.filter((col) => !columnOrder.includes(col.field)));
  }, [columns, columnOrder]);

  const handleSelectionModelChange = (newSelection) => {
    const currentPageIds = rows.map((r) => r.id);
    const otherPageIds = selectedIds.filter(
      (id) => !currentPageIds.includes(id),
    );
    const updatedSelection = [...new Set([...otherPageIds, ...newSelection])];
    setSelectedIds(updatedSelection);

    if (onSelectionChange) {
      const selectedRowsData = rows.filter((row) =>
        updatedSelection.includes(row.id),
      );
      onSelectionChange(selectedRowsData);
    }
  };

  const handleRowDoubleClick = (params) => {
    if (selectionMode === "clickToActivate" && !selectionActive) {
      setSelectionActive(true);
      const id = params.id;
      setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  const fetchAllIds = async () => {
    if (!fetchDataUrl) {
      toast.error("No data URL provided for fetching all items");
      return [];
    }

    setLoadingAllIds(true);

    try {
      let ids = [];
      const resolvedFetchDataUrl = appendTeamMemberLawyerId(
        fetchDataUrl,
        teamMemberLawyerId,
      );
      const separator = resolvedFetchDataUrl.includes("?") ? "&" : "?";
      let nextUrl = `${resolvedFetchDataUrl}${separator}page_size=${rowCount}`;

      while (nextUrl) {
        const res = await axios.get(nextUrl, {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (res?.data?.results && Array.isArray(res.data.results)) {
          ids = [...ids, ...res.data.results.map((item) => item.id)];
        } else if (Array.isArray(res?.data)) {
          ids = [...ids, ...res.data.map((item) => item.id)];
        } else if (res?.data?.data && Array.isArray(res.data.data)) {
          ids = [...ids, ...res.data.data.map((item) => item.id)];
        }

        nextUrl = res?.data?.next || null;

        if (ids.length >= rowCount) break;
      }

      return ids.slice(0, rowCount);
    } catch (error) {
      console.error("Error fetching all IDs:", error);
      toast.error("Failed to fetch all items");
      return [];
    } finally {
      setLoadingAllIds(false);
    }
  };

  const handleSelectAllAcrossPages = async () => {
    const ids = await fetchAllIds();
    if (ids.length > 0) {
      setSelectedIds(ids);
      toast.success(`Selected all ${ids.length} items`);
    }
  };

  const handleCancelSelection = () => {
    setSelectionActive(false);
    setSelectedIds([]);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const getSelectedRows = () =>
    rows.filter((row) => selectedIds.includes(row.id));

  const resolveBulkActionText = (value, count) =>
    typeof value === "function" ? value(count) : value;

  // Built-in bulk delete function
  const handleBulkDelete = async () => {
    if (!baseDeleteUrl) {
      toast.error("Delete URL not provided");
      return;
    }

    setDeleting(true);
    setDeleteProgress({
      total: selectedIds.length,
      completed: 0,
      errors: [],
      failedIds: [],
    });

    // Configure headers if token is provided
    const config = token
      ? {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      : {};

    try {
      const resolvedBulkDeleteUrl = appendTeamMemberLawyerId(
        bulkDeleteUrl || createBulkActionUrl(baseDeleteUrl, "bulk-delete"),
        teamMemberLawyerId,
      );
      const response = await axios.post(
        resolvedBulkDeleteUrl,
        { ids: selectedIds },
        config,
      );
      const summary = response?.data?.summary || {};
      const failedItems = (response?.data?.results || []).filter(
        (item) => item.status !== "deleted",
      );
      const failedIds = failedItems.map((item) => item.id).filter(Boolean);

      setDeleteProgress({
        total: selectedIds.length,
        completed: summary.deleted || 0,
        errors: failedIds,
        failedIds,
      });

      if (failedItems.length > 0 && (summary.deleted || 0) === 0) {
        toast.error(`Failed to delete ${failedItems.length} items`);
        return;
      }

      toast.success(`${summary.deleted || 0} items deleted successfully`);

      if (onRefreshData) {
        await onRefreshData();
      }

      handleCancelSelection();
      setDeleteDialogOpen(false);
    } catch (error) {
      const failedItems = error?.response?.data?.results || [];
      const failedIds = failedItems.map((item) => item.id).filter(Boolean);

      setDeleteProgress({
        total: selectedIds.length,
        completed: 0,
        errors: failedIds,
        failedIds,
      });
      toast.error(
        error?.response?.data?.detail ||
          `Failed to delete ${failedIds.length || selectedIds.length} items`,
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (!deleting) setDeleteDialogOpen(false);
  };

  const handleBulkActionClick = () => {
    setBulkActionDialogOpen(true);
  };

  const handleCloseBulkActionDialog = () => {
    if (!bulkActing) setBulkActionDialogOpen(false);
  };

  const handleBulkAction = async () => {
    if (!bulkAction?.onAction) {
      toast.error("Bulk action is not configured");
      return;
    }

    setBulkActing(true);
    try {
      await bulkAction.onAction({
        selectedIds,
        selectedRows: getSelectedRows(),
      });

      if (bulkAction.refreshAfterAction && onRefreshData) {
        await onRefreshData();
      }

      handleCancelSelection();
      setBulkActionDialogOpen(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.message ||
          resolveBulkActionText(bulkAction.errorMessage, selectedIds.length) ||
          "Bulk action failed",
      );
    } finally {
      setBulkActing(false);
    }
  };

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    try {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(newModel));
    } catch (error) {
      console.error("Error saving column visibility to localStorage:", error);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (
      dragItem.current !== null &&
      dragOverItem.current !== null &&
      dragItem.current !== dragOverItem.current
    ) {
      const newColumnOrder = [...columnOrder];
      const draggedItem = newColumnOrder.splice(dragItem.current, 1)[0];
      newColumnOrder.splice(dragOverItem.current, 0, draggedItem);

      setColumnOrder(newColumnOrder);

      try {
        const orderKey = getColumnOrderKey();
        localStorage.setItem(orderKey, JSON.stringify(newColumnOrder));
      } catch (error) {
        console.error("Error saving column order to localStorage:", error);
      }
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleOpenReorderDialog = () => {
    setReorderDialogOpen(true);
  };

  const handleCloseReorderDialog = () => {
    setReorderDialogOpen(false);
    setDragOverIndex(null);
  };

  const resetColumnOrder = () => {
    const defaultOrder = columns.map((col) => col.field);
    setColumnOrder(defaultOrder);
    try {
      const orderKey = getColumnOrderKey();
      localStorage.setItem(orderKey, JSON.stringify(defaultOrder));
    } catch (error) {
      console.error("Error resetting column order:", error);
    }
  };

  const totalPages = Math.max(1, Math.ceil((rowCount || 0) / resolvedPageSize));
  const hasFilterChips = Array.isArray(filterChips) && filterChips.length > 0;

  const handlePaginationChange = (event, value) => {
    const newPage = value;
    setPage(newPage);
    setCurrentPage(newPage);
    if (handlePageChange) {
      handlePageChange(newPage);
    } else if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleResolvedPageSizeChange = (event) => {
    const newPageSize = Number(event.target.value);

    if (handlePageSizeChange) {
      handlePageSizeChange(event);
      return;
    }

    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  const loadingOverlayProps = {
    variant: "skeleton",
    noRowsVariant: "skeleton",
  };

  const isRowClickable = !!rest.onRowClick;

  const EnhancedToolbar = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: !extraBtn ? "flex-end" : "space-between",
        position: "relative",
      }}
    >
      {extraBtn && extraBtn()}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          position: "relative",
          gap: 0.5,
        }}
      >
        {filterBtn && filterBtn()}

        {/* Arrow button to activate selection (clickToActivate mode) */}
        {selectionMode === "clickToActivate" && !selectionActive && (
          <Tooltip title="Select items" arrow>
            <IconButton size="small" onClick={() => setSelectionActive(true)}>
              <CheckBoxRounded
                fontSize="small"
                sx={{ color: "primary.main" }}
              />
            </IconButton>
          </Tooltip>
        )}

        {/* Selection mode buttons */}
        {selectionActive && (
          <>
            {fetchDataUrl && (
              <Button
                variant="text"
                color="primary"
                size="small"
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.75rem",
                  p: "0.2rem 0.5rem",
                  minWidth: "auto",
                }}
                startIcon={<SelectAllRoundedIcon sx={{ fontSize: "0.8rem" }} />}
                onClick={handleSelectAllAcrossPages}
                disabled={loadingAllIds}
              >
                {loadingAllIds ? "Loading..." : `Select all (${rowCount || 0})`}
              </Button>
            )}
            {selectedIds.length > 0 && BulkEditComponent && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.75rem",
                  p: "0.2rem 0.5rem",
                  minWidth: "auto",
                }}
                startIcon={
                  <DriveFileRenameOutlineRounded sx={{ fontSize: "0.8rem" }} />
                }
                onClick={() => {
                  const selectedRowsData = getSelectedRows();

                  showDialog(
                    React.cloneElement(BulkEditComponent, {
                      selectedIds: selectedIds,
                      selectedRows: selectedRowsData,
                      onSuccess: () => {
                        if (onRefreshData) {
                          onRefreshData();
                        }

                        handleCancelSelection();
                      },
                      isBulkEdit: true,
                      token: token,
                      teamMemberLawyerId,
                    }),
                    {
                      dialogTitle: `${editTitle || "Edit"} (${selectedIds.length} items)`,
                    },
                  );
                }}
              >
                Edit ({selectedIds.length})
              </Button>
            )}

            {selectedIds.length > 0 && baseDeleteUrl && (
              <Button
                variant="contained"
                color="error"
                size="small"
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.75rem",
                  p: "0.2rem 0.5rem",
                  minWidth: "auto",
                }}
                startIcon={<DeleteRoundedIcon sx={{ fontSize: "0.8rem" }} />}
                onClick={handleDeleteClick}
              >
                Delete ({selectedIds.length})
              </Button>
            )}

            {selectedIds.length > 0 && bulkAction?.onAction && (
              <Button
                variant="contained"
                color={bulkAction.color || "success"}
                size="small"
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.75rem",
                  p: "0.2rem 0.5rem",
                  minWidth: "auto",
                }}
                startIcon={bulkAction.icon || <RestoreRounded sx={{ fontSize: "0.8rem" }} />}
                onClick={handleBulkActionClick}
              >
                {resolveBulkActionText(bulkAction.label, selectedIds.length) ||
                  `Action (${selectedIds.length})`}
              </Button>
            )}

            <Tooltip title="Cancel selection" arrow>
              <IconButton size="small" onClick={handleCancelSelection}>
                <CloseRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        <Tooltip title="Reorder columns" arrow>
          <IconButton size="small" onClick={handleOpenReorderDialog}>
            <ReorderIcon fontSize="small" sx={{ color: "primary.main" }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset to default order" arrow>
          <IconButton size="small" onClick={resetColumnOrder}>
            <RestoreRounded fontSize="small" sx={{ color: "primary.main" }} />
          </IconButton>
        </Tooltip>
        {CustomToolbar && (
          <CustomToolbar
            selectedRows={rows.filter((r) => selectedIds.includes(r.id))}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: resolvedHeight,
        minHeight: resolvedMinHeight,
        maxHeight: resolvedHeight,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
          "& .MuiDataGrid-root": {
            height: "100%",
            maxHeight: "100%",
            overflowX: "auto",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={orderedColumns}
          paginationMode="server"
          rowCount={rowCount}
          pageSize={resolvedPageSize}
          checkboxSelection={showCheckboxes}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={handleSelectionModelChange}
          onRowDoubleClick={handleRowDoubleClick}
          disableRowSelectionOnClick
          disableSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          loading={loading}
          hideFooter={!showFooter}
          hideFooterPagination={true}
          {...rest}
          initialState={{
            pinnedColumns: {
              left: ["desk"],
            },
            density: "compact",
          }}
          slotProps={{
            loadingOverlay: loadingOverlayProps,
          }}
          slots={{
            toolbar: EnhancedToolbar,
            ...(CustomNoRowsOverlay && {
              noRowsOverlay: CustomNoRowsOverlay,
            }),
            footer: () => {
              if (!showFooter) return null;
              return (
                <Box
                  sx={{
                    p: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: hasFilterChips ? 0.75 : 0,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    {hasFilterChips && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 0.5,
                          overflowX: "auto",
                          pb: 0.25,
                          pt: 0.25,
                          pr: 0.5,
                        }}
                      >
                        {filterChips.map((chip, index) => (
                          <Box
                            key={`filter-chip-${index}`}
                            sx={{ flexShrink: 0 }}
                          >
                            {chip}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    {canChangePageSize ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Rows per page:
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={resolvedPageSize}
                            onChange={handleResolvedPageSizeChange}
                            size="small"
                            sx={{ fontSize: "0.8rem", height: 32 }}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={30}>30</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    ) : null}

                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontSize: "0.8rem", flexShrink: 0 }}
                    >
                      {rowCount
                        ? `${(currentPage - 1) * resolvedPageSize + 1}-${Math.min(
                            currentPage * resolvedPageSize,
                            rowCount,
                          )} of ${rowCount}`
                        : `0 of 0`}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      variant="outlined"
                      color="primary"
                      page={currentPage}
                      onChange={handlePaginationChange}
                      disabled={loading}
                      size="small"
                    />
                  </Box>
                </Box>
              );
            },
          }}
          sx={{
            ...pageScrollStyle,
            border: "1px solid #e5e5e5",
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            minHeight: 0,
            overflow: "hidden",
            ...(isRowClickable && {
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }),
            ".MuiDataGrid-cell": {
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#00b3b02c",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "inherit",
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-main": {
              overflow: "auto",
              scrollbarWidth: "thin",
              scrollBehavior: "smooth",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
              scrollBehavior: "smooth",
            },
          }}
        />
      </Box>

      {/* Delete Progress Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{deleteDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {deleteConfirmMessage}
          </Typography>
          {deleting && (
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(deleteProgress.completed / deleteProgress.total) * 100}
              />
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                {deleteProgress.completed} of {deleteProgress.total} completed
              </Typography>
              {deleteProgress.errors.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Failed: {deleteProgress.errors.length} items
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 100,
                      overflow: "auto",
                      mt: 1,
                      p: 1,
                      bgcolor: "#fee",
                      borderRadius: 1,
                    }}
                  >
                    {deleteProgress.failedIds.map((id) => (
                      <Typography key={id} variant="caption" display="block">
                        Failed to delete ID: {id}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCloseDeleteDialog}
            disabled={deleting}
            sx={{ textTransform: "capitalize" }}
          >
            {deleting ? "Deleting..." : "Cancel"}
          </Button>
          {!deleting && (
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleBulkDelete}
              sx={{ textTransform: "capitalize" }}
            >
              Delete {selectedIds.length} Items
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkActionDialogOpen}
        onClose={handleCloseBulkActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {resolveBulkActionText(
            bulkAction?.dialogTitle,
            selectedIds.length,
          ) || "Confirm Bulk Action"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {resolveBulkActionText(
              bulkAction?.confirmMessage,
              selectedIds.length,
            ) || "Are you sure you want to continue?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCloseBulkActionDialog}
            disabled={bulkActing}
            sx={{ textTransform: "capitalize" }}
          >
            {bulkActing ? "Working..." : "Cancel"}
          </Button>
          <Button
            size="small"
            variant="contained"
            color={bulkAction?.color || "success"}
            onClick={handleBulkAction}
            disabled={bulkActing}
            sx={{ textTransform: "capitalize" }}
          >
            {bulkActing
              ? resolveBulkActionText(
                  bulkAction?.loadingLabel,
                  selectedIds.length,
                ) || "Working..."
              : resolveBulkActionText(
                  bulkAction?.actionButtonLabel,
                  selectedIds.length,
                ) || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Reorder Dialog */}
      <Dialog
        open={reorderDialogOpen}
        onClose={handleCloseReorderDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            ...pageScrollStyle,
          },
        }}
      >
        <DialogTitle>Reorder Columns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag and drop columns to reorder them. The new order will be saved
            automatically.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {orderedColumns.map((column, index) => (
              <Box
                key={column.field}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDragEnd={handleDragLeave}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 2,
                  border: "1px solid",
                  borderColor:
                    dragOverIndex === index ? "primary.main" : "divider",
                  backgroundColor:
                    dragOverIndex === index
                      ? "action.hover"
                      : "background.paper",
                  borderRadius: 1,
                  cursor: "grab",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  "&:active": {
                    cursor: "grabbing",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <DragIndicatorIcon color="action" />
                <Typography variant="body1">
                  {column.headerName || column.field}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Button size="small" onClick={resetColumnOrder}>
            Reset to Default
          </Button>
          <Button size="small" onClick={handleCloseReorderDialog}>
            Close
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
